import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useDiagnosticAccessories,
  useDiagnosticAccessory,
  useCreateDiagnosticAccessory,
  useUpdateDiagnosticAccessory,
  useDeleteDiagnosticAccessory,
  useBulkDeleteDiagnosticAccessories,
  useBulkUpdateDiagnosticAccessories,
  useActiveSpecimenTypes,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { toast } from 'sonner';
import { generateAcronymCode, makeUniqueCode } from '../../utils/code';
import { Pagination } from '../../components/ui/Pagination';
import { DiagnosticSubmitModal } from '../../components/ui/DiagnosticSubmitModal';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { Package, Loader2, AlertCircle, Plus, Pencil, Trash2, Send, Search, X, Save, Power, PowerOff, Square, SquareCheck, Edit3, Hash, DollarSign, CheckCircle, XCircle } from 'lucide-react';

export function DiagnosticAccessories() {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [statusFilter, setStatusFilter] = useState<'all' | 'ACTIVE' | 'INACTIVE'>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [submitModal, setSubmitModal] = useState<{ open: boolean; id: string; name: string } | null>(null);
  const [formModal, setFormModal] = useState<{ mode: 'create' | 'edit'; id?: string } | null>(null);
  const [viewModal, setViewModal] = useState<{ open: boolean; id: string } | null>(null);

  const { data, isLoading, error } = useDiagnosticAccessories(page, limit, debouncedSearch || undefined);
  // All existing accessory codes, used to keep an auto-generated code unique.
  const { data: allAccessoriesData } = useDiagnosticAccessories(1, 1000);
  const deleteMutation = useDeleteDiagnosticAccessory();
  const createMutation = useCreateDiagnosticAccessory();
  const updateMutation = useUpdateDiagnosticAccessory();
  const bulkDeleteMutation = useBulkDeleteDiagnosticAccessories();
  const bulkUpdateMutation = useBulkUpdateDiagnosticAccessories();

  const { data: editAccessoryData, isLoading: editAccessoryLoading } = useDiagnosticAccessory(
    formModal?.mode === 'edit' ? formModal.id : undefined
  );
  const { data: viewData, isLoading: viewLoading, error: viewError } = useDiagnosticAccessory(
    viewModal?.open ? viewModal.id : undefined
  );
  const { data: specimenTypesData } = useActiveSpecimenTypes();
  const specimenTypes = specimenTypesData?.data || [];
  const [togglingStatus, setTogglingStatus] = useState(false);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    price: '0',
    isContainer: false,
    specimenTypeId: '',
    color: '',
    defaultVolume: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const accessories = data?.data || [];
  const meta = data?.meta;
  const trashedCount = Number(data?.stats?.trashed ?? 0);
  const existingCodes = useMemo(
    () => (allAccessoriesData?.data || []).map((item: any) => item.code).filter(Boolean) as string[],
    [allAccessoriesData],
  );

  const filteredAccessories = useMemo(() => {
    if (statusFilter === 'all') return accessories;
    return accessories.filter((acc: any) => acc.status === statusFilter);
  }, [accessories, statusFilter]);

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (formModal?.mode === 'edit' && editAccessoryData?.data) {
      const a = editAccessoryData.data;
      setFormData({
        code: a.code || '',
        name: a.name || '',
        price: a.price != null ? String(a.price) : '0',
        isContainer: !!a.isContainer,
        specimenTypeId: a.specimenTypeId || a.specimenType?.id || '',
        color: a.color || '',
        defaultVolume: a.defaultVolume || '',
      });
      setCodeManuallyEdited(true);
    } else if (formModal?.mode === 'create') {
      setFormData({ code: '', name: '', price: '0', isContainer: false, specimenTypeId: '', color: '', defaultVolume: '' });
      setCodeManuallyEdited(false);
    }
    setFormErrors({});
  }, [formModal, editAccessoryData]);

  // Auto-generate the code from the name (acronym), kept unique among existing
  // accessories and left editable — editing the code stops further generation.
  useEffect(() => {
    if (formModal?.mode !== 'create') return;
    if (!formData.name.trim()) return;
    if (codeManuallyEdited) return;
    const base = generateAcronymCode(formData.name, 20);
    if (!base) return;
    const unique = makeUniqueCode(base, existingCodes, { maxLength: 20 });
    setFormData((prev) => (prev.code === unique ? prev : { ...prev, code: unique }));
  }, [formData.name, codeManuallyEdited, formModal, existingCodes]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.code.trim()) errors.code = 'Code is required';
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (formData.price.trim() !== '' && Number(formData.price) < 0) errors.price = 'Price must be positive';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const parsedPrice = Number(formData.price);
      const payload = {
        code: formData.code,
        name: formData.name,
        price: formData.price.trim() === '' || !Number.isFinite(parsedPrice) ? 0 : parsedPrice,
        isContainer: formData.isContainer,
        specimenTypeId: formData.isContainer ? formData.specimenTypeId || undefined : undefined,
        color: formData.isContainer ? formData.color || undefined : undefined,
        defaultVolume: formData.isContainer ? formData.defaultVolume || undefined : undefined,
      };
      if (formModal?.mode === 'edit' && formModal.id) {
        await updateMutation.mutateAsync({ id: formModal.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      setFormModal(null);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      setFormErrors({ general: message });
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'code') {
      setCodeManuallyEdited(String(value).trim() !== '');
    }
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleDelete = (id: string) => {
    const acc = accessories.find((a: any) => a.id === id);
    setDeleteTarget({ id, name: acc?.name || 'this accessory' });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Accessory deleted');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete accessory');
    } finally {
      setDeleteTarget(null);
    }
  };

  const allSelected = filteredAccessories.length > 0 && filteredAccessories.every((acc: any) => selectedIds.includes(acc.id));
  const someSelected = selectedIds.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredAccessories.some((acc: any) => acc.id === id)));
    } else {
      const newIds = filteredAccessories.map((acc: any) => acc.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...newIds])));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    const ids = [...selectedIds];
    try {
      await bulkDeleteMutation.mutateAsync(ids);
      setSelectedIds([]);
      toast.success(`Deleted ${ids.length} accessor${ids.length === 1 ? 'y' : 'ies'}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete accessories');
    } finally {
      setBulkDeleteOpen(false);
    }
  };

  const handleBulkActivate = async () => {
    const ids = [...selectedIds];
    try {
      await bulkUpdateMutation.mutateAsync({ ids, data: { status: 'ACTIVE' } });
      setSelectedIds([]);
      toast.success(`Activated ${ids.length} accessor${ids.length === 1 ? 'y' : 'ies'}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to activate accessories');
    }
  };

  const handleBulkDeactivate = async () => {
    const ids = [...selectedIds];
    try {
      await bulkUpdateMutation.mutateAsync({ ids, data: { status: 'INACTIVE' } });
      setSelectedIds([]);
      toast.success(`Deactivated ${ids.length} accessor${ids.length === 1 ? 'y' : 'ies'}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to deactivate accessories');
    }
  };

  const handleToggleStatus = async () => {
    if (!viewModal?.id || !viewData?.data) return;
    setTogglingStatus(true);
    try {
      await updateMutation.mutateAsync({
        id: viewModal.id,
        data: { status: viewData.data.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' },
      });
    } finally {
      setTogglingStatus(false);
    }
  };

  const accessory = viewData?.data;

  const inputBase = 'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 text-sm transition-colors';
  const inputValid = 'border-border focus:ring-primary';
  const inputInvalid = 'border-danger focus:ring-danger';
  const labelClass = 'block text-sm font-medium text-text mb-1';
  const ic = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;
  const err = (field: string) => formErrors[field] ? <p className="text-xs text-danger mt-1">{formErrors[field]}</p> : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load diagnostic accessories</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Diagnostic Accessories</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/administration/diagnostic-accessories/trash')}
            className="px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            title="View trash"
          >
            <Trash2 size={16} />
            Trash
            {trashedCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-none">
                {trashedCount > 99 ? '99+' : trashedCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setFormModal({ mode: 'create' })}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Add Accessory
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search accessories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as any); setSelectedIds([]); }}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {someSelected && (
        <div className="flex items-center gap-2 bg-surface border border-border rounded-xl p-3">
          <span className="text-sm text-text-muted mr-2">{selectedIds.length} selected</span>
          <button
            onClick={handleBulkActivate}
            disabled={bulkUpdateMutation.isPending}
            className="px-3 py-1.5 rounded-lg bg-success text-white text-xs font-medium hover:bg-success/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Power size={12} /> Activate
          </button>
          <button
            onClick={handleBulkDeactivate}
            disabled={bulkUpdateMutation.isPending}
            className="px-3 py-1.5 rounded-lg bg-warning text-white text-xs font-medium hover:bg-warning/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <PowerOff size={12} /> Deactivate
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleteMutation.isPending}
            className="px-3 py-1.5 rounded-lg bg-danger text-white text-xs font-medium hover:bg-danger/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <button onClick={toggleSelectAll} className="text-text-muted hover:text-text transition-colors">
                    {allSelected ? <SquareCheck size={18} className="text-primary" /> : <Square size={18} />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredAccessories.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                    {search || statusFilter !== 'all' ? (
                      <span>No accessories match your filters.{' '}
                        <button onClick={() => { setSearch(''); setStatusFilter('all'); setPage(1); }} className="text-primary hover:underline">
                          Clear filters
                        </button>
                      </span>
                    ) : (
                      'No diagnostic accessories found'
                    )}
                  </td>
                </tr>
              ) : (
                filteredAccessories.map((accessory: any) => (
                  <tr key={accessory.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSelectOne(accessory.id)} className="text-text-muted hover:text-text transition-colors">
                        {selectedIds.includes(accessory.id) ? <SquareCheck size={18} className="text-primary" /> : <Square size={18} />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setViewModal({ open: true, id: accessory.id })}
                        className="font-medium text-text hover:text-primary transition-colors text-left cursor-pointer"
                      >
                        {accessory.name}
                      </button>
                      {accessory.isContainer && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
                          Container
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-text">{accessory.code}</td>
                    <td className="px-4 py-3 text-text">৳{Number(accessory.price).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          accessory.status === 'ACTIVE'
                            ? 'bg-success/10 text-success'
                            : 'bg-danger/10 text-danger'
                        }`}
                      >
                        {accessory.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setFormModal({ mode: 'edit', id: accessory.id })}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setSubmitModal({ open: true, id: accessory.id, name: accessory.name })}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Submit Doc & Data"
                        >
                          <Send size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(accessory.id)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="accessories"
        />
      </div>

      {submitModal?.open && (
        <DiagnosticSubmitModal
          entityType="diagnostic_accessory"
          entityId={submitModal.id}
          entityName={submitModal.name}
          onClose={() => setSubmitModal(null)}
        />
      )}

      {/* Create / Edit Modal */}
      {formModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface z-10 flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-text">
                  {formModal.mode === 'edit' ? 'Edit Diagnostic Accessory' : 'Add Diagnostic Accessory'}
                </h2>
              </div>
              <button
                onClick={() => setFormModal(null)}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="p-5 space-y-5">
              {formErrors.general && (
                <div className="bg-danger/10 text-danger rounded-lg p-4 flex items-center gap-2">
                  <AlertCircle size={18} />
                  <span className="text-sm font-medium">{formErrors.general}</span>
                </div>
              )}

              {formModal.mode === 'edit' && editAccessoryLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Name *</label>
                      <input
                        type="text"
                        className={ic('name')}
                        value={formData.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        placeholder="Syringe 5ml"
                      />
                      {err('name')}
                    </div>
                    <div>
                      <label className={labelClass}>
                        Code * {formModal.mode === 'create' && <span className="text-text-muted font-normal">(auto-generated)</span>}
                      </label>
                      <input
                        type="text"
                        className={`${ic('code')} font-mono`}
                        value={formData.code}
                        onChange={(e) => updateField('code', e.target.value)}
                        placeholder="ACC-01"
                      />
                      {err('code')}
                    </div>
                    <div>
                      <label className={labelClass}>Price (৳) *</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={ic('price')}
                        value={formData.price}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '' || /^\d*\.?\d*$/.test(raw)) updateField('price', raw);
                        }}
                        onFocus={(e) => {
                          if (e.target.value === '0') updateField('price', '');
                        }}
                        onBlur={(e) => {
                          const raw = e.target.value.trim();
                          if (raw === '' || !Number.isFinite(Number(raw))) updateField('price', '0');
                        }}
                        placeholder="0"
                      />
                      {err('price')}
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-sm text-text cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={formData.isContainer}
                      onChange={(e) => updateField('isContainer', e.target.checked)}
                      className="rounded border-border"
                    />
                    This item is a specimen container
                  </label>

                  {formData.isContainer && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-background border border-border rounded-lg p-4">
                      <div>
                        <label className={labelClass}>Sample Type</label>
                        <select
                          className={`${inputBase} ${inputValid}`}
                          value={formData.specimenTypeId}
                          onChange={(e) => updateField('specimenTypeId', e.target.value)}
                        >
                          <option value="">Select Sample Type</option>
                          {specimenTypes.map((type: any) => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Colour</label>
                        <input
                          type="text"
                          className={`${inputBase} ${inputValid}`}
                          value={formData.color}
                          onChange={(e) => updateField('color', e.target.value)}
                          placeholder="e.g. Purple"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Default Volume</label>
                        <input
                          type="text"
                          className={`${inputBase} ${inputValid}`}
                          value={formData.defaultVolume}
                          onChange={(e) => updateField('defaultVolume', e.target.value)}
                          placeholder="e.g. 3 mL"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => setFormModal(null)}
                  className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm flex items-center gap-2"
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending || (formModal.mode === 'edit' && editAccessoryLoading)}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {(createMutation.isPending || updateMutation.isPending) && <Loader2 size={16} className="animate-spin" />}
                  {formModal.mode === 'edit' ? <Save size={16} /> : <Plus size={16} />}
                  {formModal.mode === 'edit' ? 'Save Changes' : 'Add Accessory'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface z-10 flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-text">Accessory Details</h2>
              </div>
              <button
                onClick={() => setViewModal(null)}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {viewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : viewError || !accessory ? (
              <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger m-5">
                <AlertCircle size={18} />
                <span>Failed to load diagnostic accessory details. Please try again.</span>
              </div>
            ) : (
              <div className="p-5 space-y-5">
                {/* Profile Header Card */}
                <div className="bg-surface border border-border rounded-xl p-6">
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="flex-1 min-w-0 space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <Package size={20} className="text-primary" />
                            <h1 className="text-2xl font-bold text-text">{accessory.name}</h1>
                          </div>
                          <p className="text-sm text-text-muted mt-1 font-mono">{accessory.code}</p>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium shrink-0 ${
                            accessory.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {accessory.status === 'ACTIVE' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          {accessory.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Hash size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Code</p>
                            <p className="text-sm font-medium text-text">{accessory.code}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <DollarSign size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Price</p>
                            <p className="text-sm font-medium text-text">৳{Number(accessory.price).toLocaleString()}</p>
                          </div>
                        </div>

                        {accessory.isContainer && (
                          <>
                            <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                              <div className="p-2 bg-primary/10 rounded-lg">
                                <Package size={16} className="text-primary" />
                              </div>
                              <div>
                                <p className="text-xs text-text-muted">Container</p>
                                <p className="text-sm font-medium text-text">
                                  {[accessory.specimenType?.name, accessory.color].filter(Boolean).join(' · ') || 'Yes'}
                                </p>
                              </div>
                            </div>

                            {accessory.defaultVolume && (
                              <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                                <div className="p-2 bg-primary/10 rounded-lg">
                                  <Hash size={16} className="text-primary" />
                                </div>
                                <div>
                                  <p className="text-xs text-text-muted">Default Volume</p>
                                  <p className="text-sm font-medium text-text">{accessory.defaultVolume}</p>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Toggle */}
                <div className="bg-surface border border-border rounded-xl p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={18} className="text-primary" />
                    <h2 className="text-lg font-semibold text-text">Status Control</h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleToggleStatus}
                      disabled={togglingStatus || updateMutation.isPending}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        accessory.status === 'ACTIVE'
                          ? 'bg-success/10 text-success border border-success/20'
                          : 'bg-danger/10 text-danger border border-danger/20'
                      } disabled:opacity-50`}
                    >
                      {accessory.status === 'ACTIVE' ? <CheckCircle size={18} /> : <XCircle size={18} />}
                      {accessory.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                    </button>
                    <p className="text-xs text-text-muted">
                      {accessory.status === 'ACTIVE'
                        ? 'This accessory is available for diagnostic tests and receipts.'
                        : 'This accessory is hidden from diagnostic operations.'}
                    </p>
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="flex flex-wrap items-center justify-end gap-3 pt-4">
                  <button
                    onClick={() => {
                      setViewModal(null);
                      setFormModal({ mode: 'edit', id: accessory.id });
                    }}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <Edit3 size={16} />
                    Edit Accessory
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete "${deleteTarget?.name ?? ''}"?`}
        description="This will remove the accessory. This cannot be undone."
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selectedIds.length} accessor${selectedIds.length === 1 ? 'y' : 'ies'}?`}
        description="This action will remove the selected accessories. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.length} accessor${selectedIds.length === 1 ? 'y' : 'ies'}`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />
    </div>
  );
}
